import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { useToast } from '../../../hooks/useToast';

export default function TransportVerification() {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [providerData, setProviderData] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    vanRegistration: '',
    vanSizeTier: '',
    publicLiabilityDoc: null as File | null,
    publicLiabilityExpiry: '',
    vehicleInsuranceDoc: null as File | null,
    vehicleInsuranceExpiry: '',
  });

  useEffect(() => {
    loadProviderData();
  }, []);

  const loadProviderData = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('provider_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      setProviderData(data);
      
      // Pre-fill existing data
      if (data.van_registration) {
        setFormData(prev => ({
          ...prev,
          vanRegistration: data.van_registration || '',
          vanSizeTier: data.van_size_tier || '',
          publicLiabilityExpiry: data.public_liability_expiry_date || '',
          vehicleInsuranceExpiry: data.vehicle_insurance_expiry_date || '',
        }));
      }
    } catch (error: any) {
      console.error('Error loading provider data:', error);
      toast.error('Failed to load provider data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (field: 'publicLiabilityDoc' | 'vehicleInsuranceDoc', file: File | null) => {
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload PDF, JPG, or PNG files only');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
    }
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const validateForm = () => {
    if (!formData.vanRegistration.trim()) {
      toast.error('Please enter your van registration');
      return false;
    }
    if (!formData.vanSizeTier) {
      toast.error('Please select your van size');
      return false;
    }
    if (!formData.publicLiabilityDoc && !providerData?.public_liability_doc_url) {
      toast.error('Please upload your Public Liability Insurance document');
      return false;
    }
    if (!formData.publicLiabilityExpiry) {
      toast.error('Please enter your Public Liability Insurance expiry date');
      return false;
    }
    if (new Date(formData.publicLiabilityExpiry) < new Date()) {
      toast.error('Public Liability Insurance expiry date must be in the future');
      return false;
    }
    if (!formData.vehicleInsuranceDoc && !providerData?.vehicle_insurance_doc_url) {
      toast.error('Please upload your Vehicle Insurance document');
      return false;
    }
    if (!formData.vehicleInsuranceExpiry) {
      toast.error('Please enter your Vehicle Insurance expiry date');
      return false;
    }
    if (new Date(formData.vehicleInsuranceExpiry) < new Date()) {
      toast.error('Vehicle Insurance expiry date must be in the future');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let publicLiabilityUrl = providerData?.public_liability_doc_url;
      let vehicleInsuranceUrl = providerData?.vehicle_insurance_doc_url;

      // Upload Public Liability document if new file
      if (formData.publicLiabilityDoc) {
        const fileName = `${user.id}/public-liability-${Date.now()}.${formData.publicLiabilityDoc.name.split('.').pop()}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('provider-documents')
          .upload(fileName, formData.publicLiabilityDoc);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('provider-documents')
          .getPublicUrl(fileName);

        publicLiabilityUrl = publicUrl;
      }

      // Upload Vehicle Insurance document if new file
      if (formData.vehicleInsuranceDoc) {
        const fileName = `${user.id}/vehicle-insurance-${Date.now()}.${formData.vehicleInsuranceDoc.name.split('.').pop()}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('provider-documents')
          .upload(fileName, formData.vehicleInsuranceDoc);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('provider-documents')
          .getPublicUrl(fileName);

        vehicleInsuranceUrl = publicUrl;
      }

      // Update provider profile
      const { error: updateError } = await supabase
        .from('provider_profiles')
        .update({
          van_registration: formData.vanRegistration.toUpperCase(),
          van_size_tier: formData.vanSizeTier,
          public_liability_doc_url: publicLiabilityUrl,
          public_liability_expiry_date: formData.publicLiabilityExpiry,
          vehicle_insurance_doc_url: vehicleInsuranceUrl,
          vehicle_insurance_expiry_date: formData.vehicleInsuranceExpiry,
          transport_verification_status: 'pending_verification',
          transport_docs_submitted_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      toast.success('Verification documents submitted successfully! We\'ll review them shortly.');
      loadProviderData();
    } catch (error: any) {
      console.error('Error submitting verification:', error);
      toast.error(error.message || 'Failed to submit verification');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Transport Verification</h2>
          <p className="text-gray-600">
            Upload your vehicle details and insurance documents. These are required before you can receive transportation and moving jobs.
          </p>
        </div>

        {providerData?.transport_verification_status === 'rejected_verification' && providerData?.transport_rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
              <div>
                <h3 className="font-semibold text-red-900 mb-1">Verification Rejected</h3>
                <p className="text-sm text-red-700">{providerData.transport_rejection_reason}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Vehicle Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Vehicle Details</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Van Registration <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.vanRegistration}
                onChange={(e) => setFormData({ ...formData, vanRegistration: e.target.value.toUpperCase() })}
                placeholder="AB12 CDE"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Van Size <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.vanSizeTier}
                onChange={(e) => setFormData({ ...formData, vanSizeTier: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              >
                <option value="">Select van size</option>
                <option value="small">Small Van</option>
                <option value="medium">Medium Van</option>
                <option value="large">Large Van</option>
                <option value="xl">Extra Large Van</option>
              </select>
            </div>
          </div>

          {/* Public Liability Insurance */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-900">Public Liability Insurance</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Insurance Document <span className="text-red-500">*</span>
              </label>
              {providerData?.public_liability_doc_url && !formData.publicLiabilityDoc && (
                <div className="mb-2 text-sm text-green-600 flex items-center gap-2">
                  <i className="ri-check-line"></i>
                  Document uploaded
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('publicLiabilityDoc', e.target.files?.[0] || null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG (max 10MB)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.publicLiabilityExpiry}
                onChange={(e) => setFormData({ ...formData, publicLiabilityExpiry: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Vehicle Insurance */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-900">Vehicle Insurance</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Insurance Document <span className="text-red-500">*</span>
              </label>
              {providerData?.vehicle_insurance_doc_url && !formData.vehicleInsuranceDoc && (
                <div className="mb-2 text-sm text-green-600 flex items-center gap-2">
                  <i className="ri-check-line"></i>
                  Document uploaded
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('vehicleInsuranceDoc', e.target.files?.[0] || null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG (max 10MB)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.vehicleInsuranceExpiry}
                onChange={(e) => setFormData({ ...formData, vehicleInsuranceExpiry: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <i className="ri-shield-check-line text-xl text-blue-600 mt-0.5"></i>
              <p className="text-sm text-blue-700">
                Insurance documents are stored securely and are not shared publicly.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </span>
            ) : (
              'Submit for Review'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
